/**
 * Sprint S13.1.B.1 — Step 2: chronic conditions + medications.
 *
 * All fields optional. "other:" prefix on free-text entries lets future
 * deserialization separate from controlled vocab without ambiguity.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { FieldMultiSelect } from "@/components/onboarding/fields/FieldMultiSelect";


// Controlled vocab — values are stable backend identifiers, labels are i18n
const HEALTH_CONDITION_OPTIONS = [
  "lumbar_disc_l4_l5",
  "back_pain_chronic",
  "diabetes_t2",
  "hypertension",
  "asthma",
  "hashimoto",
  "ibs",
  "anxiety_disorder",
];

const MEDICATION_OPTIONS = [
  "blood_pressure_medication",
  "thyroid_medication",
  "diabetes_medication",
  "anti_inflammatory",
  "antidepressant",
  "asthma_inhaler",
];


function splitControlledOther(values: string[]): {
  controlled: string[];
  other: string;
} {
  const controlled: string[] = [];
  const otherParts: string[] = [];
  for (const v of values) {
    if (v.startsWith("other:")) {
      otherParts.push(v.slice(6));
    } else {
      controlled.push(v);
    }
  }
  return { controlled, other: otherParts.join(", ") };
}


export function Step2HealthContext() {
  const { t } = useTranslation();
  const data = useOnboardingStore((s) => s.data);
  const setField = useOnboardingStore((s) => s.setField);
  const nextStep = useOnboardingStore((s) => s.nextStep);

  const initialConditions = splitControlledOther(data.health_conditions);
  const initialMeds = splitControlledOther(data.medications);

  const [otherCondition, setOtherCondition] = useState(initialConditions.other);
  const [otherMedication, setOtherMedication] = useState(initialMeds.other);

  const conditionOptions = HEALTH_CONDITION_OPTIONS.map((v) => ({
    value: v,
    label: t(`onboarding.step.health_context.condition.${v}`),
  }));
  const medicationOptions = MEDICATION_OPTIONS.map((v) => ({
    value: v,
    label: t(`onboarding.step.health_context.medication.${v}`),
  }));

  const controlledConditions = data.health_conditions.filter(
    (v) => !v.startsWith("other:"),
  );
  const controlledMeds = data.medications.filter((v) => !v.startsWith("other:"));

  const handleNext = () => {
    // Persist final state с merged "other:" entries
    const conditions = [...controlledConditions];
    if (otherCondition.trim()) conditions.push(`other:${otherCondition.trim()}`);
    setField("health_conditions", conditions);

    const meds = [...controlledMeds];
    if (otherMedication.trim()) meds.push(`other:${otherMedication.trim()}`);
    setField("medications", meds);

    nextStep();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2
          id="step-heading"
          tabIndex={-1}
          className="mb-2 text-xl font-semibold text-navy-deep sm:text-2xl"
        >
          {t("onboarding.step.health_context.title")}
        </h2>
        <p className="text-[13px] text-taupe sm:text-[14px]">
          {t("onboarding.step.health_context.subtitle")}
        </p>
        <p className="mt-1 text-[12px] text-taupe-light">
          {t("onboarding.step.health_context.skip_hint")}
        </p>
      </div>

      <FieldMultiSelect
        label={t("onboarding.step.health_context.conditions_label")}
        hint={t("onboarding.step.health_context.conditions_hint")}
        options={conditionOptions}
        selected={controlledConditions}
        onChange={(values) => setField("health_conditions", values)}
        allowOther
        otherValue={otherCondition}
        onOtherChange={setOtherCondition}
        otherPlaceholder={t("onboarding.step.health_context.other_placeholder")}
      />

      <FieldMultiSelect
        label={t("onboarding.step.health_context.medications_label")}
        hint={t("onboarding.step.health_context.medications_hint")}
        options={medicationOptions}
        selected={controlledMeds}
        onChange={(values) => setField("medications", values)}
        allowOther
        otherValue={otherMedication}
        onOtherChange={setOtherMedication}
        otherPlaceholder={t("onboarding.step.health_context.other_med_placeholder")}
      />

      <div className="pt-2">
        <Button
          type="button"
          onClick={handleNext}
          data-onboarding-next
          className="w-full sm:w-auto"
        >
          {t("onboarding.nav.next")}
        </Button>
      </div>
    </div>
  );
}
